// src/services/sanitaryDailyClosingService.js
// =============================================================================
// Frontend API wrapper for automated sanitary daily closing
// =============================================================================

import apiClient from "../utils/apiClient";

const DEBUG_API = String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

function getErrorMessage(err) {
  return err?.response?.data?.message || err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Request failed";
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);
  e.status = err?.response?.status;
  e.code = err?.response?.data?.code || err?.code;
  e.data = err?.response?.data;
  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[SanitaryDailyClosingService] ${context}`, { status: e.status, code: e.code, data: e.data });
  }
  throw e;
}

function normalizeListPayload(payload, keys = []) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

function buildParams(params = {}) {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""));
}

export async function fetchSanitaryDailyClosingSettings() {
  try {
    const res = await apiClient.get("/sanitary/daily-closing/settings");
    return res.data;
  } catch (err) {
    throwApiError("fetchSanitaryDailyClosingSettings", err);
  }
}

export async function updateSanitaryDailyClosingSettings(payload = {}) {
  try {
    const res = await apiClient.put("/sanitary/daily-closing/settings", payload);
    return res.data;
  } catch (err) {
    throwApiError("updateSanitaryDailyClosingSettings", err);
  }
}

export async function fetchSanitaryDailyClosingRuns(params = {}) {
  try {
    const res = await apiClient.get("/sanitary/daily-closing/runs", { params: buildParams(params) });
    return normalizeListPayload(res.data, ["runs"]);
  } catch (err) {
    throwApiError("fetchSanitaryDailyClosingRuns", err);
  }
}

export async function runSanitaryDailyClosing(payload = {}) {
  try {
    const res = await apiClient.post("/sanitary/daily-closing/run", payload);
    return res.data;
  } catch (err) {
    throwApiError("runSanitaryDailyClosing", err);
  }
}

export default {
  fetchSanitaryDailyClosingSettings,
  updateSanitaryDailyClosingSettings,
  fetchSanitaryDailyClosingRuns,
  runSanitaryDailyClosing,
};
